import React, { useState, useCallback, useRef } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  startOfDay,
  endOfDay
} from 'date-fns'
import { Button } from './ui/button'
import { cn } from '../lib/utils'

interface WorkoutCalendarProps {
  selectedDateRange: { start: Date; end: Date } | null
  onDateRangeChange: (range: { start: Date; end: Date } | null) => void
  onClose?: () => void
}

export default function WorkoutCalendar({
  selectedDateRange,
  onDateRangeChange,
  onClose
}: WorkoutCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState<Date | null>(null)
  const [dragEnd, setDragEnd] = useState<Date | null>(null)
  const _calendarRef = useRef<HTMLDivElement>(null)

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  // Get the current selection range
  const currentRange = React.useMemo(() => {
    if (isDragging && dragStart) {
      if (!dragEnd || isSameDay(dragStart, dragEnd)) {
        return { start: dragStart, end: dragStart }
      }
      return {
        start: dragStart < dragEnd ? dragStart : dragEnd,
        end: dragStart < dragEnd ? dragEnd : dragStart
      }
    }
    return selectedDateRange
  }, [isDragging, dragStart, dragEnd, selectedDateRange])

  const handleMouseDown = useCallback((day: Date, e: React.MouseEvent) => {
    e.preventDefault() // Prevent text selection
    e.stopPropagation() // Prevent event bubbling
    setIsDragging(true)
    setDragStart(day)
    setDragEnd(day)
    // Don't set the range yet - wait for mouse up
  }, [])

  const handleMouseEnter = useCallback(
    (day: Date) => {
      if (isDragging && dragStart) {
        setDragEnd(day)
      }
    },
    [isDragging, dragStart]
  )

  const handleMouseUp = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (isDragging && dragStart && dragEnd) {
        const start = dragStart < dragEnd ? dragStart : dragEnd
        const end = dragStart < dragEnd ? dragEnd : dragStart
        onDateRangeChange({ start: startOfDay(start), end: endOfDay(end) })
      } else if (isDragging && dragStart) {
        // Single day click
        onDateRangeChange({ start: startOfDay(dragStart), end: endOfDay(dragStart) })
      }

      setIsDragging(false)
      setDragStart(null)
      setDragEnd(null)
    },
    [isDragging, dragStart, dragEnd, onDateRangeChange]
  )

  // Global mouse up handler
  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => {
        handleMouseUp()
      }
      document.addEventListener('mouseup', handleGlobalMouseUp)
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging, handleMouseUp])

  const clearSelection = useCallback(() => {
    onDateRangeChange(null)
    setDragStart(null)
    setDragEnd(null)
    setIsDragging(false)
  }, [onDateRangeChange])

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1))
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1))
  const goToToday = () => setCurrentMonth(new Date())

  const isDaySelected = (day: Date) => {
    if (!currentRange) return false
    return isWithinInterval(day, { start: currentRange.start, end: currentRange.end })
  }

  const isDayStart = (day: Date) => {
    if (!currentRange) return false
    return isSameDay(day, currentRange.start)
  }

  const isDayEnd = (day: Date) => {
    if (!currentRange) return false
    return isSameDay(day, currentRange.end)
  }

  const isCurrentMonth = (day: Date) => {
    return day.getMonth() === currentMonth.getMonth()
  }

  return (
    <div className="w-[350px]">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth} className="h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="w-[140px] text-center text-base font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <Button variant="ghost" size="icon" onClick={goToNextMonth} className="h-8 w-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={currentRange ? clearSelection : goToToday}
              className="h-7 text-xs"
            >
              {currentRange ? 'Clear' : 'Today'}
            </Button>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Calendar Grid */}
        <div
          className={cn('grid grid-cols-7 gap-0.5 select-none', isDragging && 'cursor-crosshair')}
          style={{ userSelect: 'none' }}
        >
          {/* Day headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-muted-foreground flex h-8 items-center justify-center text-center text-xs font-medium"
            >
              {day}
            </div>
          ))}

          {/* Days */}
          {days.map((day, dayIdx) => {
            const selected = isDaySelected(day)
            const isStart = isDayStart(day)
            const isEnd = isDayEnd(day)
            const isToday = isSameDay(day, new Date())
            const inCurrentMonth = isCurrentMonth(day)

            return (
              <div
                key={dayIdx}
                onMouseDown={(e) => handleMouseDown(day, e)}
                onMouseEnter={() => handleMouseEnter(day)}
                onMouseUp={(e) => handleMouseUp(e)}
                className={cn(
                  'relative h-9 w-full text-sm transition-colors duration-75 select-none',
                  isDragging ? 'cursor-crosshair' : 'cursor-pointer',
                  !inCurrentMonth && 'text-muted-foreground opacity-50'
                )}
              >
                <div
                  className={cn(
                    'absolute inset-0.5 flex items-center justify-center transition-colors',
                    'hover:bg-accent/30',
                    selected && 'bg-primary/20 hover:bg-primary/30',
                    (isStart || isEnd) && 'bg-primary text-primary-foreground hover:bg-primary/90',
                    isToday && !selected && 'ring-primary/50 ring-1',
                    isToday && (isStart || isEnd) && 'ring-primary-foreground/50 ring-1',
                    // Rounded corners logic
                    !selected && 'rounded-md',
                    selected && !isStart && !isEnd && 'rounded-none',
                    isStart && !isEnd && 'rounded-l-md rounded-r-none',
                    isEnd && !isStart && 'rounded-l-none rounded-r-md',
                    isStart && isEnd && 'rounded-md'
                  )}
                >
                  <span className={cn(isToday && 'font-semibold')}>{format(day, 'd')}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Selected Range Display */}
        {currentRange && (
          <div className="text-muted-foreground pt-2 text-center text-xs">
            {format(currentRange.start, 'MMM d, yyyy')}
            {!isSameDay(currentRange.start, currentRange.end) && (
              <> - {format(currentRange.end, 'MMM d, yyyy')}</>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
